import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { Container, Card, Button, Spinner, Alert, Badge, Row, Col } from 'react-bootstrap';
import { FaHome, FaSearch, FaUser } from 'react-icons/fa';
import { protectedResources, scopes } from "../authConfig";
import useFetchWithMsal from '../hooks/useFetchWithMsal';

export const CompanyUsers = () => {
    const navigate = useNavigate();
    const { instance } = useMsal();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [companyId, setCompanyId] = useState(null);
    const [companyName, setCompanyName] = useState('');

    const { execute } = useFetchWithMsal({
        scopes: scopes.callapi
    });

    // Fetch company users
    const fetchUsers = useCallback(async () => {
        if (!companyId) return;
        
        setLoading(true);
        setError(null);
        
        try {
            // This would need a new API endpoint - for now we'll use getUsers and filter
            const response = await execute('GET', protectedResources.getUsers.endpoint);
            
            if (response && Array.isArray(response)) {
                // Filter users that belong to the company
                const companyUsers = response.filter(user => 
                    user.extension_551d375da0774dac9fbf7ba98ba05b33_CompanyId === companyId || 
                    user.companyId === companyId
                );
                setUsers(companyUsers);
            } else {
                setUsers([]);
                if (response && response.message) {
                    setError(response.message);
                }
            }
        } catch (error) {
            console.error("Error fetching company users:", error);
            setError("ユーザー情報の取得中にエラーが発生しました。");
        } finally {
            setLoading(false);
        }
    }, [companyId, execute]);

    // Initialize component
    useEffect(() => {
        const activeAccount = instance?.getActiveAccount();
        
        // Check permission type - only allow HR managers (type 3)
        const permissionType = activeAccount?.idTokenClaims?.extension_551d375da0774dac9fbf7ba98ba05b33_PermissionType || 
                              activeAccount?.idTokenClaims?.extension_PermissionType;
        
        if (permissionType !== 3 && permissionType !== 4) { // Allow HR managers and admins
            navigate('/error');
            return;
        }
        
        // Set company information from user's token claims
        const userCompanyId = activeAccount?.idTokenClaims?.extension_551d375da0774dac9fbf7ba98ba05b33_CompanyId || 
                             activeAccount?.idTokenClaims?.extension_CompanyId;
                             
        if (userCompanyId) {
            setCompanyId(userCompanyId);
            
            // Get company name if available in claims
            const userCompanyName = activeAccount?.idTokenClaims?.companyName || 
                                   activeAccount?.idTokenClaims?.extension_CompanyName;
            if (userCompanyName) {
                setCompanyName(userCompanyName);
            }
        } else {
            setError("ユーザーに関連付けられた企業情報が見つかりません。");
            setLoading(false);
        }
    }, [instance, navigate]);

    // Fetch users when companyId is set
    useEffect(() => {
        if (companyId) {
            fetchUsers();
        }
    }, [companyId, fetchUsers]);

    // Get user role display name
    const getUserRole = (user) => {
        // Check both possible field names for permission type
        const permissionType = user.extension_551d375da0774dac9fbf7ba98ba05b33_PermissionType || 
                              user.PermissionType || 
                              user.extension_PermissionType;
                              
        const roles = {
            "1": { name: "一般ユーザー", variant: "info" },
            "2": { name: "事務局", variant: "primary" },
            "3": { name: "人事担当", variant: "success" },
            "4": { name: "管理者", variant: "danger" }
        };
        
        const role = roles[permissionType] || { name: "未定義", variant: "secondary" };
        return <Badge bg={role.variant}>{role.name}</Badge>;
    };

    return (
        <Container className="py-5">
            <Card className="border-0 shadow-sm mb-5">
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <div>
                            <h1 className="fw-bold mb-0">自社ユーザー一覧</h1>
                            {companyName && <p className="text-muted mb-0">{companyName}</p>}
                        </div>
                        <Button 
                            variant="outline-secondary" 
                            className="d-flex align-items-center gap-2" 
                            onClick={() => navigate('/hr-manager')}
                        >
                            <FaHome /> ホームへ戻る
                        </Button>
                    </div>
                </Card.Body>
            </Card>

            {error && (
                <Alert variant="danger" className="mb-4">
                    {error}
                </Alert>
            )}

            <Card className="border-0 shadow-sm">
                <Card.Header className="bg-light py-3">
                    <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center gap-2">
                            <h5 className="mb-0">ユーザー一覧</h5>
                            {loading && <Spinner animation="border" size="sm" />}
                        </div>
                        <div className="d-flex gap-2">
                            <Button 
                                variant="outline-primary" 
                                className="d-flex align-items-center gap-2"
                                onClick={() => fetchUsers()}
                                disabled={loading}
                            >
                                <FaSearch /> 更新
                            </Button>
                        </div>
                    </div>
                </Card.Header>
                
                <Card.Body>
                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" />
                            <p className="mt-2">ユーザーデータを読み込み中...</p>
                        </div>
                    ) : users.length === 0 ? (
                        <div className="text-center py-5">
                            <p className="text-muted">ユーザーデータがありません。</p>
                        </div>
                    ) : (
                        <Row>
                            {users.map((user) => (
                                <Col md={6} lg={4} key={user.id} className="mb-3">
                                    <Card>
                                        <Card.Body className="d-flex">
                                            <div className="me-3">
                                                {user.profilePictureUrl ? (
                                                    <img 
                                                        src={user.profilePictureUrl} 
                                                        alt={user.displayName || 'User'} 
                                                        style={{ width: '60px', height: '60px', borderRadius: '50%', objectFit: 'cover' }}
                                                    />
                                                ) : (
                                                    <div style={{ 
                                                        width: '60px', 
                                                        height: '60px', 
                                                        borderRadius: '50%', 
                                                        backgroundColor: '#e9ecef', 
                                                        display: 'flex', 
                                                        alignItems: 'center', 
                                                        justifyContent: 'center' 
                                                    }}>
                                                        <FaUser size={25} color="#adb5bd" />
                                                    </div>
                                                )}
                                            </div>
                                            <div>
                                                <h5 className="mb-1">{user.displayName || `${user.givenName} ${user.surname}`}</h5>
                                                <p className="text-muted mb-1">{user.loginName || user.mail || user.userPrincipalName || '-'}</p>
                                                <div>{getUserRole(user)}</div>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    )}
                </Card.Body>
            </Card>
        </Container>
    );
};

export default CompanyUsers;
